/** Login: local credentials plus any configured SAML providers. */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getProviders } from "@pbxmgr/api";
import { Alert, Button, Card, TextField } from "@pbxmgr/ui";

import { useAuth } from "../auth/AuthProvider";
import { useBranding } from "../branding";

export function LoginPage(): ReactNode {
  const { session, login, expired } = useAuth();
  // Where RequireAuth interrupted the user; return there after sign-in.
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
  const branding = useBranding();
  const navigate = useNavigate();
  const providers = useQuery({ queryKey: ["providers"], queryFn: getProviders, retry: false });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to={from} replace />;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-login">
      <Card>
        <h1>{branding.product_name}</h1>
        {branding.login_text ? <p>{branding.login_text}</p> : null}
        {expired ? (
          <Alert>Your session expired — sign in again to continue.</Alert>
        ) : null}
        {error ? <Alert>{error}</Alert> : null}
        <form onSubmit={onSubmit}>
          <TextField
            id="username"
            label="Username"
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            id="password"
            label="Password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        {providers.data && providers.data.length > 0 ? (
          <div className="ops-login__providers">
            {providers.data.map((p) => (
              <a key={p.id} href={`/api/auth/saml/${p.id}/login`}>
                Sign in with {p.name}
              </a>
            ))}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
